/** @jsx jsx */
import { Grid, Spacer } from "@cd2/components"
import { jsx, css } from "@emotion/core"
import React, { useState } from "react"
import { useHistory } from "react-router-dom"
import { breadCrumbs } from "../../constants/breadCrumbs"
import { PageWrapper } from "../../interfaceParts/PageWrapper"
import { IRecord } from "../../types/schema"
import { buildRoute, eRoutes, routes } from "../../routes"
import { icons } from "../../utils/icons"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { LoadingFrame } from "../user/UserResultsShowPage/AssessmentDetailsTab"
import { useSkeem } from "../../providers/SkeemProvider"
import { useLocationReloadKey, useLocationReloadTrigger } from "../../hooks/locationReloadKey"
import { CGButton } from "../../interfaceParts/CGButton"
import { useTheme } from "@cd2/theme"
import { ErrorBoundary } from "../../ErrorBoundary"
import { LinkButton } from "../../interfaceParts/LinkButton"
import { assert } from "@cd2/tools"
import { CompanyTagsInlineEditor } from "../../interfaceParts/CompanyTagsInlineEditor"

export function AdminCompanyShowWrapper({
  companyId,
  active,
  children,
}: {
  companyId: string
  active: string
  children: React.ReactNode
}) {
  type Company = IRecord<
    "companies",
    | "name"
    | "archived"
    | "isAdminCompany"
    | "isHeadCompany"
    | "isSubCompany"
    | "freeAccount"
    | "enableFourthIntegration"
    | "enableITrentIntegration"
    | "disableEmployeeFeatures"
  > & {
    headCompany: IRecord<"companies", "name"> | null
    plan: IRecord<"companyPlans", "name"> | null
    companyTags: IRecord<"companyTags", "name">[]
  }

  const skeem = useSkeem()
  const reloadKey = useLocationReloadKey()
  const triggerReload = useLocationReloadTrigger()
  const history = useHistory()
  const { themeColor } = useTheme()

  const [company, setCompany] = useState<Company | null>(null)

  useAsyncEffect(async () => {
    const company = await skeem.fetch2<Company | null>({
      first: {
        name: "companies",
        attributes: [
          "name",
          "archived",
          "isAdminCompany",
          "isHeadCompany",
          "isSubCompany",
          "freeAccount",
          "enableFourthIntegration",
          "enableITrentIntegration",
          "disableEmployeeFeatures",
          { name: "headCompany", attributes: ["name"] },
          { name: "plan", attributes: ["name"] },
          { name: "companyTags", attributes: ["name"] },
        ],
        matches: { id: companyId },
      },
    })
    assert(company, "Company not found")
    setCompany(company)
  }, [companyId, reloadKey])

  if (!company) return <LoadingFrame />

  const title = company?.name || "-"

  return (
    <PageWrapper
      breadcrumbs={[
        breadCrumbs.dashboard,
        breadCrumbs.companies,
        { name: `Edit ${title}`, to: "#" },
      ]}
      pre="Edit Company"
      title={title}
      afterActions={
        <div
          css={css`
            max-width: 720px;
          `}
        >
          <CompanyTagsInlineEditor
            companyId={companyId}
            initialTags={company.companyTags}
            onUpdated={triggerReload}
          />
        </div>
      }
    >
      {company.isAdminCompany && (
        <div
          css={css`
            background-color: #ffcccc;
            padding: 16px;
            border-radius: 4px;
            color: #ff0000;
            margin-top: 16px;
            font-size: 24px;
          `}
        >
          This is the special admin company which super admins (like you) are assigned to.
        </div>
      )}
      {!company.freeAccount && !company.plan && !company.isSubCompany && (
        <div
          css={css`
            background-color: #ffcccc;
            padding: 16px;
            border-radius: 4px;
            color: #ff0000;
            margin-top: 16px;
            font-size: 24px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
          `}
        >
          This company does not have a plan and is not marked as a free account.
          <LinkButton to={buildRoute(eRoutes["/companies/:id/billing"], { id: company.id })}>
            View Billing
          </LinkButton>
        </div>
      )}
      {company.archived && (
        <div
          css={css`
            background-color: #ffcccc;
            padding: 16px;
            border-radius: 4px;
            color: #ffc800;
            margin-top: 16px;
            font-size: 24px;
          `}
        >
          This company is archived!
        </div>
      )}
      {company.isHeadCompany && (
        <div
          css={css`
            background-color: #77f8a2;
            padding: 16px;
            border-radius: 4px;
            margin-top: 16px;
            font-size: 24px;
          `}
        >
          This is a head company that manages multiple sub-companies.
        </div>
      )}
      {company.isSubCompany && (
        <div
          css={css`
            background-color: #77f8a2;
            padding: 16px;
            border-radius: 4px;
            margin-top: 16px;
            font-size: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span>This is a sub-company of {company.headCompany?.name ?? "- unknown -"}.</span>
          <LinkButton to={buildRoute(eRoutes["/companies/:id"], { id: company.headCompany?.id })}>
            View head company
          </LinkButton>
        </div>
      )}
      <Spacer size={32} />
      <div>
        <Grid columns={[180, { min: 0, max: { fr: 1 } }]} gap={32} alignItems="start">
          <Grid gap={6} alignItems="center" columns={["auto"]}>
            {[
              {
                icon: "chart-line",
                key: "stats",
                title: "Stats",
                to: buildRoute(eRoutes["/companies/:id"], { id: companyId }),
              },
              {
                icon: "edit",
                key: "details",
                title: "Details",
                to: buildRoute(eRoutes["/companies/:id/details"], { id: companyId }),
              },
              {
                icon: icons.subCompanies,
                key: "sub-companies",
                title: "Sub-Companies",
                to: buildRoute(eRoutes["/companies/:id/sub-companies"], { id: companyId }),
                hide: !company.isHeadCompany,
              },
              {
                icon: icons.users,
                key: "users",
                title: "Users",
                to: `/companies/${companyId}/users`,
              },
              {
                icon: icons.employees,
                key: "employees",
                title: "Employees",
                to: `/companies/${companyId}/employees`,
                redirect: true,
                hide: company.disableEmployeeFeatures,
              },
              {
                icon: "users-cog",
                key: "teams",
                title: "Teams",
                to: `/companies/${companyId}/teams`,
                redirect: true,
                disabled: company.isSubCompany,
              },
              {
                icon: "tasks",
                key: "tasks",
                title: "User Tasks",
                to: `/companies/${companyId}/tasks`,
                redirect: true,
                disabled: company.isSubCompany,
              },
              {
                icon: icons.departments,
                key: "departments",
                title: "Departments",
                to: routes.adminCompaniesDepartments(companyId),
                redirect: true,
                disabled: company.isSubCompany,
              },
              {
                icon: "tags",
                key: "employee-tags",
                title: "Employee Tags",
                to: `/companies/${companyId}/employee-tags`,
                disabled: company.isSubCompany,
                hide: company.disableEmployeeFeatures,
              },
              {
                icon: "cog",
                key: "settings",
                title: "Platform Functions",
                to: buildRoute(eRoutes["/companies/:id/settings"], { id: companyId }),
              },
              {
                icon: "shield-alt",
                key: "sso",
                title: "SSO",
                to: `/companies/${companyId}/sso`,
                hide: company.isSubCompany,
              },
              {
                icon: "bell",
                key: "alerts",
                title: "Alerts",
                to: buildRoute(eRoutes["/companies/:id/alerts"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                icon: icons.helpRequests,
                key: "help-requests",
                title: "Help Requests",
                to: `/companies/${companyId}/help-requests`,
                disabled: company.isSubCompany,
              },
              {
                key: "files",
                title: "Files",
                icon: "file",
                to: buildRoute(eRoutes["/companies/:id/files"], { id: companyId }),
              },
              {
                icon: "briefcase",
                key: "manageFlows",
                title: "Manage Processes",
                to: buildRoute(eRoutes["/companies/:id/manage-flows"], { id: companyId }),
              },
              {
                icon: icons.billing,
                key: "billing",
                title: "Billing",
                to: buildRoute(eRoutes["/companies/:id/billing"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                icon: "link",
                key: "links",
                title: "Internal Resources",
                to: buildRoute(eRoutes["/companies/:id/links"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                icon: "palette",
                key: "whitelabel",
                title: "White-Labelling",
                to: buildRoute(eRoutes["/companies/:id/whitelabel"], { id: companyId }),
              },
              {
                icon: "history",
                key: "history",
                title: "History",
                to: buildRoute(eRoutes["/companies/:id/history"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                key: "locations",
                icon: "map",
                title: "Locations",
                to: buildRoute(eRoutes["/companies/:id/locations"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                key: "archive",
                icon: "times",
                title: "Archive",
                to: buildRoute(eRoutes["/companies/:id/archive"], { id: companyId }),
                disabled: company.isAdminCompany || company.isHeadCompany,
              },
              {
                icon: "receipt",
                key: "plan",
                title: "OLD - Plan",
                to: buildRoute(eRoutes["/companies/:id/plan"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                icon: "file-invoice-dollar",
                key: "subscription",
                title: "OLD - Subscription",
                to: buildRoute(eRoutes["/companies/:id/subscription"], { id: companyId }),
                disabled: company.isSubCompany,
              },
              {
                icon: icons.fourth,
                key: "fourth",
                title: "Fourth Integration",
                to: buildRoute(eRoutes["/companies/:id/fourth"], { id: companyId }),
                hide: !company.enableFourthIntegration,
              },
              {
                icon: icons.link,
                key: "itrent",
                title: "iTrent Integration",
                to: buildRoute(eRoutes["/companies/:id/itrent"], { id: companyId }),
                hide: !company.enableITrentIntegration || !company.isHeadCompany,
              },
            ]
              .filter(tab => !tab.hide)
              .map(tab => (
                <div
                  key={tab.key}
                  css={css`
                    border: initial !important;
                    border-bottom: 2px solid
                      ${tab.key === active ? themeColor("primary") : "transparent"} !important;
                    button {
                      div {
                        margin: 0;
                        display: block;
                      }
                    }
                  `}
                >
                  <CGButton
                    children={tab.title}
                    buttonStyle="text"
                    onClick={() => history.push(tab.to)}
                    extendTheme={{ padding: 7, fontSize: 12, borderRadius: 2 }}
                    leftIcon={tab.icon}
                    rightIcon={tab.redirect ? "external-link-alt" : undefined}
                    disabled={tab.disabled}
                    block
                  />
                </div>
              ))}
          </Grid>
          <ErrorBoundary>
            <div>{children}</div>
          </ErrorBoundary>
        </Grid>
      </div>
    </PageWrapper>
  )
}
